import type {
  BtlAddress,
  BtlApplication,
  BtlApplicationType,
  BtlPurpose,
  ExpectedPropertyType,
  PaymentProfile,
  PropertyAttributes,
  PropertySubType,
  RepaymentStrategy,
  ResidenceRegion,
  UpdateEstateAgent,
  UpsertIntermediaryFeeDto,
  UpdateCaseDto,
} from '@api/btlApplicationForm/types';
import { PropertyTenure, PropertyType, RefinancePurpose } from '@api/btlApplicationForm/types';
import type { ExistingFirmResponse } from '@api/firm/firm.types';
import { mapFormSubmissionFunnelTypeToApi } from '@pages/ApplicationFormPage/Submission/submissionRouteHelpers';
import type { SubmissionFunnelTypeEnum } from '@pages/ApplicationFormPage/Submission/types';
import {
  transformAddress,
  hasCompleteAddressEntry,
} from '@pages/BtlApplicationFormPage/common/Address/transformAddress';
import { buildIntroducerPayloadFromFormValues } from './btlIntroducerHelpers';
import type { DepositSourceFormValue } from './depositSourcesHelpers';
import { calculateDepositAmount, normalizeDepositSourcesForSubmit } from './depositSourcesHelpers';

/**
 * The fields this builder reads out of the merged AntD form values. The form layer hands over an
 * untyped bag, so the shape is asserted once inside the builder instead of at every read site.
 * The index signature keeps the pass-through keys (e.g. `fullAddress-<postcode>`) addressable.
 */
interface FinanceAndPropertyFormValues {
  [key: string]: unknown;
  purpose?: BtlPurpose;
  refinancePurpose?: RefinancePurpose | null;
  financingAmount?: number;
  outstandingBalance?: number | null;
  purchasePrice?: number;
  grossMonthlyRentalPayment?: number;
  paymentProfile?: PaymentProfile | null;
  propertyType?: PropertyType;
  propertySubType?: PropertySubType | null;
  expectedPropertyType?: ExpectedPropertyType | null;
  region?: ResidenceRegion | null;
  tenure?: PropertyTenure;
  yearsRemainingOnLease?: number | null;
  estateAgent?: UpdateEstateAgent;
  numberOfBedrooms?: number | null;
  numberOfUnits?: number | null;
  repaymentStrategy?: RepaymentStrategy | null;
  repaymentStrategyDetails?: string | null;
  submissionFunnelType?: SubmissionFunnelTypeEnum;
  depositSources?: DepositSourceFormValue[];
}

interface BuildUpdateFinanceAndPropertyDtoParams {
  caseId: string;
  propertyId?: string;
  mergedValues: Record<string, unknown>;
  data?: BtlApplication;
  firm?: ExistingFirmResponse | null;
  /**
   * Only the form that actually owns and edits deposit sources should send them. `mergedValues`
   * is built by spreading hydrated server data underneath the submitted form values, so a form
   * that doesn't render deposit-source fields would otherwise echo the server's own (possibly
   * stale) data straight back on every submit, silently overwriting a newer set. Defaults to
   * `false` so callers must opt in explicitly.
   */
  includeDepositSources?: boolean;
}

function normalizeFeesAddedToBalance(value: unknown, fallback: boolean): boolean {
  if (Array.isArray(value)) {
    return value[0] ?? false;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  return fallback;
}

function normalizeTermInMonths(value: unknown, fallbackMonths: number): number {
  if (typeof value !== 'number') {
    return fallbackMonths;
  }

  // Hydrated form values are in years; submitted form values are transformed to months.
  if (value <= 40) {
    return value * 12;
  }

  return value;
}

function normalizeCheckboxValue(value: unknown): boolean | null | undefined {
  if (Array.isArray(value)) {
    return value[0] ?? false;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  return undefined;
}

/**
 * Backend accepts `address: null` for EXPECTED properties and preserves the existing
 * address in the domain model — see strideup-be case.e2e.ts finance-and-property tests.
 */
function resolvePropertyAddress(mergedValues: Record<string, unknown>): BtlAddress | null {
  if (!hasCompleteAddressEntry(mergedValues)) {
    return null;
  }

  return transformAddress(mergedValues, true);
}

function omitUndefined<T extends Record<string, unknown>>(value: T): Partial<T> {
  return Object.fromEntries(Object.entries(value).filter(([, fieldValue]) => fieldValue !== undefined)) as Partial<T>;
}

interface ResolvePropertyAttributesParams {
  applicationType?: BtlApplicationType;
  propertyType?: PropertyType;
  formValues: FinanceAndPropertyFormValues;
  existingAttributes?: PropertyAttributes | null;
  hasCommunalSpace: boolean | null;
}

/**
 * The backend merges the `attributes` it receives into the stored ones, so each form sends only
 * the attribute fields it actually collects. BTL collects them for HMO (bedrooms + communal
 * space) and MUB (units) alone, whereas residential cases collect bedrooms for every property
 * type.
 */
function resolvePropertyAttributes({
  applicationType,
  propertyType,
  formValues,
  existingAttributes,
  hasCommunalSpace,
}: ResolvePropertyAttributesParams): Partial<PropertyAttributes> | undefined {
  if (propertyType === PropertyType.HMO) {
    return {
      numberOfBedrooms: formValues.numberOfBedrooms ?? existingAttributes?.numberOfBedrooms ?? null,
      hasCommunalSpace,
    };
  }

  if (propertyType === PropertyType.MUB) {
    return { numberOfUnits: formValues.numberOfUnits ?? existingAttributes?.numberOfUnits ?? null };
  }

  if (applicationType !== 'OWNER_OCCUPIED') {
    return undefined;
  }

  const numberOfBedrooms = formValues.numberOfBedrooms ?? existingAttributes?.numberOfBedrooms;

  // Sending `null` here would clear the stored value, so stay quiet until there is a number.
  return typeof numberOfBedrooms === 'number' ? { numberOfBedrooms } : undefined;
}

export function normalizeIntermediaryFees(
  hasIntermediaryFees: unknown,
  fees: unknown,
): UpsertIntermediaryFeeDto[] | undefined {
  if (hasIntermediaryFees === undefined && fees === undefined) {
    return undefined;
  }

  if (hasIntermediaryFees !== 'true') {
    return [];
  }

  if (!Array.isArray(fees)) {
    return [];
  }

  return fees.map(fee => {
    const normalized: UpsertIntermediaryFeeDto = {
      description: String(fee.description ?? ''),
      amount: Number(fee.amount) || 0,
      whenPayable: String(fee.whenPayable ?? ''),
      howPayable: String(fee.howPayable ?? ''),
      refundableAmount: Number(fee.refundableAmount) || 0,
    };

    if (typeof fee.id === 'string' && fee.id) {
      normalized.id = fee.id;
    }

    return normalized;
  });
}

export function buildUpdateFinanceAndPropertyDto({
  caseId,
  propertyId,
  mergedValues,
  data,
  firm,
  includeDepositSources = false,
}: BuildUpdateFinanceAndPropertyDtoParams): UpdateCaseDto {
  const existingProperty = data?.applicationProperty?.property;
  const formValues = mergedValues as FinanceAndPropertyFormValues;

  const purpose = formValues.purpose ?? data?.purpose;
  const refinancePurpose =
    purpose === 'PURCHASE' ? null : formValues.refinancePurpose ?? data?.refinancePurpose ?? null;
  const financingAmount = formValues.financingAmount ?? data?.financingAmount;
  const paymentProfile = formValues.paymentProfile ?? data?.paymentProfile ?? null;
  const resolvedPropertyType = formValues.propertyType ?? existingProperty?.propertyType;
  const resolvedHasCommunalSpace =
    normalizeCheckboxValue(formValues.hasCommunalSpace) ?? existingProperty?.attributes?.hasCommunalSpace ?? null;

  const outstandingBalance =
    refinancePurpose === RefinancePurpose.LIKE_FOR_LIKE
      ? financingAmount
      : formValues.outstandingBalance ?? data?.outstandingBalance ?? null;

  const purchasePrice = formValues.purchasePrice ?? existingProperty?.purchasePrice ?? 0;
  const property = omitUndefined({
    purchasePrice,
    grossMonthlyRentalPayment: formValues.grossMonthlyRentalPayment ?? existingProperty?.grossMonthlyRentalPayment ?? 0,
    address: resolvePropertyAddress(mergedValues),
    propertyType: resolvedPropertyType || undefined,
    propertySubType: formValues.propertySubType ?? existingProperty?.propertySubType ?? undefined,
    expectedPropertyType: formValues.expectedPropertyType ?? existingProperty?.expectedPropertyType ?? undefined,
    // The backend derives region from the property postcode when it is absent from the
    // request, so only a form that renders a region field may send one — falling back to
    // the stored region would pin the old value after an address change.
    region: formValues.region,
    attributes: resolvePropertyAttributes({
      applicationType: data?.applicationType,
      propertyType: resolvedPropertyType,
      formValues,
      existingAttributes: existingProperty?.attributes,
      hasCommunalSpace: resolvedHasCommunalSpace,
    }),
  });

  const propertyData = omitUndefined({
    property,
    tenure: formValues.tenure,
    yearsRemainingOnLease: formValues.yearsRemainingOnLease,
    estateAgent: formValues.estateAgent,
  });

  const submissionFunnelType = mapFormSubmissionFunnelTypeToApi(formValues.submissionFunnelType);
  const introducer = firm ? buildIntroducerPayloadFromFormValues(mergedValues, firm) : undefined;
  const intermediaryFees = normalizeIntermediaryFees(mergedValues.hasIntermediaryFees, mergedValues.fees);

  const isPurchase = purpose === 'PURCHASE';
  const depositAmount = isPurchase
    ? calculateDepositAmount(purchasePrice, financingAmount)
    : data?.depositAmount ?? null;
  const depositSources =
    isPurchase && includeDepositSources ? normalizeDepositSourcesForSubmit(formValues.depositSources) : undefined;

  return omitUndefined({
    caseId,
    propertyId: propertyId ?? existingProperty?.id,
    financeData: {
      purpose,
      refinancePurpose,
      feesAddedToBalance: normalizeFeesAddedToBalance(formValues.feesAddedToBalance, data?.feesAddedToBalance ?? false),
      productId: data?.product?.id ?? null,
      termInMonths: normalizeTermInMonths(formValues.termInMonths, data?.termInMonths ?? 0),
      depositAmount,
      ...(depositSources !== undefined ? { depositSources } : {}),
      financingAmount,
      outstandingBalance: isPurchase ? null : outstandingBalance,
      paymentProfile,
      repaymentStrategy: formValues.repaymentStrategy ?? null,
      repaymentStrategyDetails: formValues.repaymentStrategyDetails ?? null,
    },
    propertyData,
    submissionFunnelType,
    introducer,
    ...(intermediaryFees !== undefined ? { intermediaryFees } : {}),
  }) as UpdateCaseDto;
}
